/**
 * The two overlays the design uses: a side panel sliding in from the right (new
 * experiment, one item) and a centred dialog (add agent, custom evaluator).
 *
 * Both close on Escape and on a click on the backdrop, and both are labelled by
 * their title so tests and screen readers find them as `dialog "<title>"`.
 */

import { useEffect, useId, useRef, type ReactNode, type RefObject } from "react";
import { X } from "lucide-react";

function useEscape(onClose: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
}

const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/**
 * Keep Tab inside the overlay while it is open, move focus into it (unless a
 * field there already took it with `autoFocus`), and hand focus back to
 * whatever opened it on close — the WAI-ARIA dialog pattern.
 */
function useFocusTrap(box: RefObject<HTMLElement | null>) {
  // Read while rendering: by the time effects run, a field's `autoFocus` has moved focus.
  const opener = useRef(document.activeElement instanceof HTMLElement ? document.activeElement : null);
  useEffect(() => {
    const back = opener.current;
    const el = box.current;
    if (el && !el.contains(document.activeElement)) {
      const first = el.querySelector<HTMLElement>(`.overlay-body ${FOCUSABLE}`);
      (first ?? el).focus();
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !box.current) return;
      const items = [...box.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (!items.length) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const inside = box.current.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (back?.isConnected) back.focus();
    };
  }, [box]);
}

interface OverlayProps {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  actions?: ReactNode;
  /** Makes the dialog a form: Enter in a field submits it, through its
   *  `type="submit"` button. */
  onSubmit?: () => void;
}

function Head({ id, title, subtitle, actions, onClose }: OverlayProps & { id: string }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-line-soft px-6 pt-5 pb-4">
      <div className="flex min-w-0 flex-col gap-1">
        <h2 id={id} className="text-[17px] leading-snug font-semibold">
          {title}
        </h2>
        {subtitle && <span className="text-[13px] text-muted">{subtitle}</span>}
      </div>
      <div className="flex shrink-0 gap-2">
        {actions}
        <button type="button" className="btn btn-icon" aria-label="Close" onClick={onClose}>
          <X size={14} strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
}

function Footer({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 border-t border-line-soft bg-well px-6 py-4">{children}</div>
  );
}

export function SidePanel({ width = 560, ...props }: OverlayProps & { width?: number }) {
  const id = useId();
  const box = useRef<HTMLDivElement>(null);
  useEscape(props.onClose);
  useFocusTrap(box);
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-[var(--panel-backdrop)]">
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        className="flex-1 cursor-default"
        onClick={props.onClose}
      />
      {/* A div: `role="dialog"` isn't allowed on <aside> (axe aria-allowed-role). */}
      <div
        ref={box}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        style={{ width }}
        className="flex h-full max-w-full flex-col bg-surface shadow-[var(--panel-shadow)]"
      >
        <Head id={id} {...props} />
        <div className="overlay-body flex flex-1 flex-col gap-5 overflow-auto px-6 py-5">
          {props.children}
        </div>
        {props.footer && <Footer>{props.footer}</Footer>}
      </div>
    </div>
  );
}

export function Dialog({ width = 620, ...props }: OverlayProps & { width?: number }) {
  const id = useId();
  const box = useRef<HTMLElement>(null);
  useEscape(props.onClose);
  useFocusTrap(box);
  const { onSubmit } = props;
  const shared = {
    role: "dialog",
    "aria-modal": true,
    "aria-labelledby": id,
    tabIndex: -1,
    style: { width },
    className:
      "flex max-h-[90vh] max-w-full flex-col overflow-hidden rounded-xl bg-surface shadow-[var(--dialog-shadow)]",
  } as const;
  const content = (
    <>
      <Head id={id} {...props} />
      <div className="overlay-body flex flex-col gap-4 overflow-auto px-6 py-5">{props.children}</div>
      {props.footer && <Footer>{props.footer}</Footer>}
    </>
  );
  return (
    <div
      className="fixed inset-0 z-40 grid grid-cols-[minmax(0,1fr)] place-items-center bg-[var(--dialog-backdrop)] p-4"
      onMouseDown={(e) => e.target === e.currentTarget && props.onClose()}
    >
      {onSubmit ? (
        <form
          ref={(el) => void (box.current = el)}
          {...shared}
          onSubmit={(e) => (e.preventDefault(), onSubmit())}
        >
          {content}
        </form>
      ) : (
        <div ref={(el) => void (box.current = el)} {...shared}>
          {content}
        </div>
      )}
    </div>
  );
}
